import { formatDateTime } from '../../../../utils/hubspotUtils';
import { AssigneeAvatar } from '../../../hubspot-management/table/HubspotTicketCells';
import {
  panelInputClassName,
  primaryActionClassName,
} from '../../controlStyles';
import { LoadingSection } from '../../layout/LoadingSection';
import { PanelSection } from '../../PanelSection';
import type { HubspotTicketDetailData } from '../../types';

interface HubspotTicketNotesSectionProps {
  isLoading: boolean;
  notes: HubspotTicketDetailData['internalNotes'];
  noteBody: string;
  isNoteSaving: boolean;
  noteError: string | null;
  onNoteBodyChange: (value: string) => void;
  onSaveNote: () => void;
}

export function HubspotTicketNotesSection({
  isLoading,
  notes,
  noteBody,
  isNoteSaving,
  noteError,
  onNoteBodyChange,
  onSaveNote,
}: HubspotTicketNotesSectionProps) {
  return (
    <LoadingSection isLoading={isLoading}>
      <PanelSection
        title="Internal notes"
        trailing={
          <span className="text-xs text-neutral-500">
            Visible to the moderation team only
          </span>
        }
      >
        <div className="space-y-4 text-sm">
          <div className="space-y-2">
            <textarea
              aria-label="Internal note"
              value={noteBody}
              onChange={(event) => onNoteBodyChange(event.target.value)}
              rows={3}
              maxLength={5000}
              className={`${panelInputClassName} resize-y`}
              placeholder="Leave a note for other reviewers…"
            />
            <div className="flex items-center justify-end gap-3">
              {noteError && (
                <div className="mr-auto text-xs text-red-400">{noteError}</div>
              )}
              <button
                type="button"
                onClick={onSaveNote}
                disabled={isNoteSaving || noteBody.trim().length === 0}
                className={primaryActionClassName}
              >
                {isNoteSaving ? 'Posting…' : 'Post note'}
              </button>
            </div>
          </div>

          {notes.length === 0 ? (
            <div className="text-neutral-500">No notes yet.</div>
          ) : (
            <ol className="space-y-3">
              {notes.map((note) => (
                <li key={note.id} className="flex gap-3">
                  <AssigneeAvatar
                    name={note.author.fullName}
                    className="mt-0.5 size-7 text-[11px]"
                  />
                  <div className="min-w-0 flex-1 rounded-lg rounded-tl-sm border border-white/5 bg-white/[0.02] px-3 py-2">
                    <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
                      <span className="font-medium text-neutral-200">
                        {note.author.fullName}
                      </span>
                      <span className="text-neutral-500">
                        {formatDateTime(note.createdAt)}
                      </span>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-neutral-200">
                      {note.body}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </PanelSection>
    </LoadingSection>
  );
}
